import { Trans, useTranslation } from 'react-i18next';
import { getLocaleList } from './utils/localeList';
import useReveal from './hooks/useReveal';
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatWeDo from "./components/WhatWeDo";
import StablecoinsAndTokenization from "./components/StablecoinsAndTokenization";
import AiConsulting from "./components/AiConsulting";
import WhoWeHelp from "./components/WhoWeHelp";
import WhyPaltaLabs from "./components/WhyPaltaLabs";
import { CALENDAR_URL } from "./constants/contact";

/**
 * Section order is fixed by the agreed landing structure:
 * hero → stablecoins/tokenization/compliance → what we do → AI consulting →
 * who we help → why (the products AND the team) → CTA.
 * The three tracks come first because they are the headline's own words: the
 * hero sells stablecoins, tokenization and compliance, so that is the first
 * claim the visitor tries to verify, and it has to be answered before anything
 * else. "What we do" and AI consulting stay adjacent because they are two
 * halves of one answer to "what can you do for me?". "Who we help" absorbed the
 * old standalone use-cases section, so each case now sits inside the client it
 * describes, and "Why PaltaLabs" absorbed the team for the same reason: the
 * products we run and the people who run them are one argument about whether
 * this team can pull it off. The products live inside "why" as the evidence behind it. The team
 * is the last thing read before the booking button, so the founders are who the
 * visitor has in mind when they click.
 *
 * The standalone "Engagement" section was removed. The packages did not
 * disappear with it: "Who we help" already listed the ones that fit each
 * client, resolved by name against `engagement.packages`, so that locale list
 * stays and is now its only consumer. What went is the second, undifferentiated
 * pass over all six of them right before the CTA.
 */
export default function App() {
  const { t } = useTranslation();
  const promises = getLocaleList(t, 'hero.promises');
  // El hero queda fuera del reveal a propósito: está sobre el fold y es el
  // LCP de la página; esconderlo para volver a mostrarlo retrasa la única
  // medición de velocidad que un visitante nota.
  const ctaReveal = useReveal();

  return (
    <div className="relative text-white w-full overflow-hidden">
     <div className="relative w-full z-30">
     <Header/>

    {/* Hero Section Starts*/}
    <div className="mainCont relative mt-10 z-30">
      {/* The banner carries the composition on wide screens only. Below md2 it
          is hidden and the copy flows normally: its 1907/819 box is far too
          short at phone widths to hold the headline plus the subtitle, and
          bg2.svg already covers the background there.

          The artwork is a glow rendered on solid black, so it needs the radial
          mask to sit on the page: without it the PNG's black box cuts a visible
          rectangle out of the bg2/bg3 grid behind it. `mix-blend-screen` would
          be the cleaner fix but does nothing here — the hero wrapper carries
          `z-30`, which makes it a stacking context, so the blend never reaches
          the background images painted outside it. The mask stops at 100%
          rather than tighter because the outer objects (the avocado, the cube)
          live near the edges of the frame.

          The vignette below it darkens the middle of the frame, where the
          brightest object in the artwork (the centre coin) sits directly under
          the headline. It fades to transparent by 70% so the outer objects keep
          their full glow. */}
      <div className="relative">
        <img
          src="/images/banner-paltalbas.png"
          alt=""
          draggable="false"
          className="hidden md2:block w-full opacity-90"
          style={{
            maskImage: 'radial-gradient(ellipse 72% 68% at 50% 50%, #000 55%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 72% 68% at 50% 50%, #000 55%, transparent 100%)',
          }}
        />

        <div
          aria-hidden="true"
          className="hidden md2:block absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.72)_0%,rgba(0,0,0,0.45)_45%,transparent_70%)]"
        />

        <div className="md2:absolute md2:inset-0 flex flex-col items-center justify-center text-center py-12 md2:py-0">
          <div className="w-fit rounded-full bg-[#FFE065] border border-[#EDC113] px-4 py-1 mb-6">
            <span className="font-grotesk text-black whitespace-nowrap text-[clamp(11px,1.55vw,22px)]">
              {t('hero.badge')} <span className="font-bold">palta</span>labs
            </span>
          </div>

          <h1 className="font-grotesk uppercase leading-[1.05] text-[clamp(24px,4.6vw,64px)]">
            <span className="block font-light text-white">{t('hero.line1')}</span>
            <span className="block font-bold text-white">{t('hero.line2')}</span>
            <span className="block font-bold text-[#AFF26E]">{t('hero.line3')}</span>
          </h1>

          {/* The objection the visitor is actually holding, answered before they
              scroll. It reads as a chip rather than a sentence so the eye
              catches it on the way past the headline. It is also the only copy
              left under the headline: the explanatory subtitle was removed so
              the hero makes one claim and one promise, nothing else. */}
          <ul className="flex flex-wrap justify-center gap-3 mt-7">
            {promises.map((promise) => (
              <li
                key={promise}
                className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-full px-4 py-1.5 text-white text-[clamp(12px,1.15vw,15px)]"
              >
                <span className="text-main mr-2">✓</span>
                {promise}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>

    {/* Every CTA on the page books time directly — the header pill, this one,
        the one in "Who we help" and the one in the CTA section all point at
        CALENDAR_URL. None of them scrolls any more, so the hero button's label
        had to change with its destination. */}
    <div className="flex justify-center mt-8 z-30 relative">
      <a
        href={CALENDAR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-main transition hover:bg-main-hover text-black font-extrabold rounded-full px-8 py-3 text-lg"
      >
        {t('hero.cta')}
      </a>
    </div>

    <img src="/images/bg3.svg" alt="" draggable="false" className="absolute -top-5 left-1/2 -translate-x-1/2 w-full max-w-[1500px] hidden lg3:flex" />
    <img src="/images/bg2.svg" alt="" draggable="false" className="absolute top-0 left-0 w-full h-[430px] sm4:h-[500px] sm3:h-[600px] sm:h-[700px] md2:h-[900px] object-cover object-bottom flex lg3:hidden" />
     {/* Hero Section Ends*/}

    {/* 02 Stablecoins, tokenization and insured vaults — what the headline promises */}
    <StablecoinsAndTokenization/>

    {/* 03 What We Do */}
    <WhatWeDo/>

    {/* 04 AI Consulting */}
    <AiConsulting/>

    {/* 05 Who We Help — absorbs the use cases, one client per tab */}
    <WhoWeHelp/>

    {/* 06 Why PaltaLabs — absorbs the products, which are the evidence */}
    <WhyPaltaLabs/>

    {/* Anchored to the page wrapper, not to a section: `top-[800px]` is measured
        from the top of the document, so this has to stay a direct child here.
        Dropping it inside a `relative` section re-anchors it and moves the wash. */}
    <img src="/images/pear3.svg" alt="" draggable="true" className="absolute top-[800px] left-0 w-full h-[1500px] block" style={{opacity:0.08, zIndex:0}} />

    {/* Decorative, and anchored to the page wrapper rather than to a section
        for the same reason as pear3 below: `top-[30px]` is measured from the
        top of the document. It came out of AboutUs when the team became a
        sub-block of "Why PaltaLabs" — inside that `relative` section it would
        have re-anchored and jumped up the page. */}
    <img src="/images/circle3.svg" alt="" draggable="false" className="absolute top-[30px] -left-[120px] h-[1000px]" />

    {/* 07 CTA Section Starts*/}
    <div id="contact-us" className="mainCont relative pt-5 mt-[100px] lg4:mt-[150px]" ref={ctaReveal} data-reveal>

      {/* Single centred column: booking a meeting is the section's only job now,
          so there is no second column to balance against. */}
      <div className="relative z-20 flex flex-col items-center text-center">
        <div className="text-main font-grotesk text-[25px] uppercase font-light mb-2">{t('contact.eyebrow')}</div>
        <h2 className="text-[35px] sm3:text-[45px] font-grotesk font-bold leading-[105%] text-white uppercase">
          <Trans i18nKey="contact.title" components={{ br: <br /> }} />
        </h2>
        <p className="text-main-gray mt-5 max-w-[620px]">{t('contact.intro')}</p>

        <a
          href={CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex mt-8 bg-main transition hover:bg-main-hover text-black font-extrabold rounded-full px-8 py-3 text-lg"
        >
          {t('contact.calendarCta')}
        </a>
      </div>

      <img src="/images/large-pear.svg" alt="" draggable={false} className="absolute z-0 -bottom-[200px] left-1/2 -translate-x-1/2 w-full sm:w-auto sm:min-w-[800px] sm:max-w-[800px] pointer-events-none" />
    </div>
    {/* 08 CTA Section Ends*/}


     <Footer/>
     </div>

     {/* background */}
     <img src="/images/bg.png" alt="bg" className="absolute top-0 left-0 w-full h-full object-cover" />
    </div>
  )
}
